import React, { useEffect, useState, useRef } from 'react';
import { Video, Phone, PhoneOff } from 'lucide-react';

/**
 * Video Call Ring Modal
 * Shows when psychologist initiates a video call
 * Displays with ringing animation and sound
 */
const VideoCallRingModal = ({ 
  isOpen, 
  psychologistName, 
  onAccept, 
  onDecline 
}) => {
  const [ringCount, setRingCount] = useState(0);
  const ringIntervalRef = useRef(null);
  const audioRef = useRef(null); // Single ref for managing audio

  useEffect(() => {
    if (isOpen) {
      console.log('🔔 Video call ring modal opened');
      // Play ringing sound
      startRinging();
      
      // Animate ring count
      const animationInterval = setInterval(() => {
        setRingCount(prev => (prev + 1) % 4);
      }, 500);

      return () => {
        console.log('🔕 Video call ring modal cleanup');
        stopRinging();
        clearInterval(animationInterval);
      };
    } else {
      stopRinging();
      setRingCount(0);
    }
  }, [isOpen]);

  const startRinging = () => {
    stopRinging(); // Clear any existing ringing
    playRingSound();
    // Schedule next ring
    ringIntervalRef.current = setInterval(() => {
      playRingSound();
    }, 2000);
  };

  const stopRinging = () => {
    console.log('Stopping ring sound');
    // Clear interval
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
    
    // Stop any playing audio
    if (audioRef.current) {
      try {
        audioRef.current.close();
        audioRef.current = null;
      } catch (error) {
        console.log('Error closing audio:', error);
      }
    }
  };

  const playRingSound = () => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioRef.current = audioContext;
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Auto-close audio context after sound finishes
      setTimeout(() => {
        if (audioContext === audioRef.current) {
          audioContext.close();
          audioRef.current = null;
        }
      }, 600);
    } catch (error) {
      console.log('Could not play ring sound:', error);
    }
  };

  const handleAcceptClick = () => {
    stopRinging();
    onAccept();
  };

  const handleDeclineClick = () => {
    stopRinging();
    onDecline();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-scaleIn">
        {/* Animated Ring Icon */}
        <div className="flex justify-center mb-6">
          <div className={`relative ${ringCount % 2 === 0 ? 'scale-110' : 'scale-100'} transition-transform duration-300`}>
            <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75"></div>
            <div className="relative bg-green-500 rounded-full p-6">
              <Video className="w-16 h-16 text-white animate-pulse" />
            </div>
          </div>
        </div>

        {/* Call Information */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Incoming Video Call
          </h2>
          <p className="text-lg text-gray-600">
            {psychologistName || 'Psychologist'} is calling...
          </p>
          <p className="text-sm text-gray-500 mt-2">
            🔔 {Array(ringCount + 1).fill('•').join(' ')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          {/* Accept Button */}
          <button
            onClick={handleAcceptClick}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <Phone className="w-6 h-6" />
            Accept
          </button>

          {/* Decline Button */}
          <button
            onClick={handleDeclineClick}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <PhoneOff className="w-6 h-6" />
            Decline
          </button>
        </div>

        {/* Additional Info */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Make sure your camera and microphone are ready
        </p>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default VideoCallRingModal;
